import { useState } from 'react';
import type { ExerciseFacets, Facet } from '../../data/repositories/exerciseRepository';
import type { TaxonomyLabels } from '../../data/repositories/taxonomyRepository';
import styles from './ExerciseFilterBar.module.css';

/**
 * Facet filters — Favourites, Body Part, Target Muscle, Equipment.
 *
 * Lives under `ui/components` (not `features/exercises`) so it can be shared
 * across features without a layering violation: `ExercisePickerDialog` (also
 * `ui/components`, used from both the active-workout and plan-builder
 * "Add Exercise" flows) needs the identical filter set the Exercises tab
 * already has, and nothing under `ui/components` may import from
 * `features/*` (see that dialog's own doc comment). Moved here rather than
 * duplicated (2026-09-15) — `ExercisesScreen` now imports this same file.
 *
 * One selection per facet, combinable across facets (Phase 4A decision G).
 * Every chip is a real `<button>` with `aria-pressed`, so its state is exposed
 * to assistive technology rather than implied by colour — and the pressed style
 * changes weight and border as well as colour (Phase 0 §16).
 *
 * Options come from `exerciseRepository.facets()`, which reads the database, so
 * a user-created exercise's equipment appears here without this component
 * changing. Labels come from the taxonomy; an unknown slug falls back
 * to a readable de-slugified form rather than rendering blank.
 *
 * Per-facet counts are deliberately NOT rendered (UX pass, 2026-09-05): they
 * added visual noise to every chip for information the user rarely acted on.
 * `Facet.count` is still produced by the repository and still drives which
 * facets appear at all — this is a display-only change, and no filtering or
 * counting logic was touched.
 *
 * Archive removed from the user-facing app (2026-09-15): this used to also
 * render an "Include archived" chip. That toggle is gone — an exercise
 * archived before this change is simply no longer distinguished from any
 * other exercise, so there is nothing left here for it to control.
 */

function FacetGroup({
  legend,
  facets,
  selected,
  onToggle,
  label,
}: {
  legend: string;
  facets: Facet[];
  selected: string | null;
  onToggle: (slug: string) => void;
  label: (slug: string) => string;
}): React.JSX.Element | null {
  if (facets.length === 0) return null;

  return (
    <fieldset className={styles.facetGroup}>
      <legend className={styles.facetLegend}>{legend}</legend>
      <div className={styles.facetChips}>
        {facets.map((facet) => {
          const isSelected = selected === facet.slug;
          return (
            <button
              key={facet.slug}
              type="button"
              className={isSelected ? `${styles.filterChip} ${styles.filterChipOn}` : styles.filterChip}
              aria-pressed={isSelected}
              onClick={() => onToggle(facet.slug)}
            >
              {label(facet.slug)}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

export function ExerciseFilterBar({
  facets,
  labels,
  bodyPart,
  targetMuscle,
  equipment,
  favouritesOnly,
  onToggleBodyPart,
  onToggleTargetMuscle,
  onToggleEquipment,
  onToggleFavourites,
  onClearAll,
  hasActiveFilters,
  collapsibleFacets = false,
}: {
  facets: ExerciseFacets | null;
  labels: TaxonomyLabels | null;
  bodyPart: string | null;
  targetMuscle: string | null;
  equipment: string | null;
  favouritesOnly: boolean;
  onToggleBodyPart: (slug: string) => void;
  onToggleTargetMuscle: (slug: string) => void;
  onToggleEquipment: (slug: string) => void;
  onToggleFavourites: () => void;
  onClearAll: () => void;
  hasActiveFilters: boolean;
  /**
   * Hides the Body part / Target muscle / Equipment groups behind a
   * "Filters" toggle, starting collapsed unless one of them already has a
   * selection. The Exercises tab has a full page to spend on always-visible
   * filters; `ExercisePickerDialog` does not — on a phone, three facet
   * groups above the list left almost no height for it, to the point of
   * showing a single row before you had to scroll. Favourites and Clear all
   * stay visible either way; they're one row and worth keeping in reach.
   */
  collapsibleFacets?: boolean;
}): React.JSX.Element {
  const labelFor =
    (kind: 'bodyPart' | 'muscle' | 'equipment') =>
    (slug: string): string =>
      labels === null ? slug : labels[kind](slug);

  const anyFacetSelected = bodyPart !== null || targetMuscle !== null || equipment !== null;
  // `anyFacetSelected` only seeds the INITIAL state — open already if a
  // filter came in pre-selected. Using it as a standing override instead
  // (`facetsOpen || anyFacetSelected`) was the bug: with any filter active,
  // the toggle button still flipped `facetsOpen` on click, but the panel
  // never visually closed, because the override kept forcing it open
  // regardless. Clearing the filters then dropped that override and the
  // list snapped shut on its own, which read as "closing happens on Clear,
  // not on the toggle." The toggle button must always be able to
  // open/close it, active filters or not — the selected chip still shows
  // its state once reopened, so nothing is hidden by collapsing.
  const [facetsOpen, setFacetsOpen] = useState(anyFacetSelected);
  const showFacets = !collapsibleFacets || facetsOpen;

  // Target muscle + equipment share a wrapper only in the non-collapsible
  // (Exercises tab) presentation — the picker dialog stays a fixed ~28rem
  // wide regardless of the viewport, so it has no "desktop" state for a
  // two-column row to apply to, and always keeps every facet group on its
  // own line. On the Exercises tab, this wrapper is what turns three stacked
  // rows into two on a wide screen (`.pairedFacets` in the stylesheet) —
  // Body part alone on the first, Target muscle + Equipment side by side on
  // the second, each still scrolling horizontally on its own if it has more
  // options than the row is wide. Below the desktop breakpoint the wrapper
  // has no layout effect at all, so mobile is unchanged either way.
  const targetAndEquipment = (
    <>
      <FacetGroup
        legend="Target muscle"
        facets={facets?.targetMuscles ?? []}
        selected={targetMuscle}
        onToggle={onToggleTargetMuscle}
        label={labelFor('muscle')}
      />
      <FacetGroup
        legend="Equipment"
        facets={facets?.equipment ?? []}
        selected={equipment}
        onToggle={onToggleEquipment}
        label={labelFor('equipment')}
      />
    </>
  );

  const facetGroups = facets !== null && (
    <>
      <FacetGroup
        legend="Body part"
        facets={facets.bodyParts}
        selected={bodyPart}
        onToggle={onToggleBodyPart}
        label={labelFor('bodyPart')}
      />
      {collapsibleFacets ? (
        targetAndEquipment
      ) : (
        <div className={styles.pairedFacets}>{targetAndEquipment}</div>
      )}
    </>
  );

  return (
    <div className={styles.filters}>
      <div className={styles.facetChips}>
        <button
          type="button"
          className={favouritesOnly ? `${styles.filterChip} ${styles.filterChipOn}` : styles.filterChip}
          aria-pressed={favouritesOnly}
          onClick={onToggleFavourites}
        >
          ★ Favourites
        </button>
        {collapsibleFacets && facets !== null && (
          <button
            type="button"
            className={styles.filterChip}
            aria-expanded={showFacets}
            onClick={() => setFacetsOpen((open) => !open)}
          >
            {showFacets ? 'Fewer filters ▲' : 'More filters ▼'}
          </button>
        )}
        {hasActiveFilters && (
          <button type="button" className={styles.clearButton} onClick={onClearAll}>
            Clear all
          </button>
        )}
      </div>

      {showFacets && facetGroups}
    </div>
  );
}
